import { MutationTree } from "vuex";
import { IAppState } from "./state";
import * as mutationTypes from "./mutations-types";

import {
    IActivation,
    IActivationType,
    IActivationCase,
    IActivationCreate,
    IMarket,
    IDashboard,
    IUser,
    IEntity,
    IRole,
    IEntityType,
    IKeyword,
    IHashtag,
    IBrand,
    IBrandQualityWithChildren,
    IInfluencer,
    IActivationAccessUpdate,
    IActivationComment,
    IBrandQuality,
    IActivationUser,
    IActivationUserPermission,
    IPrAffiliate,
    IPrAffiliateCreate,
} from "@/models";
import { IUploadFile } from "@/models/IUploadFile";
import { ActivationStatus } from "@/enums";

export const mutations: MutationTree<IAppState> = {
    [mutationTypes.ACTIVATION_TYPE]: (state, payload: IActivationType[]) => {
        state.activationType = payload;
    },

    [mutationTypes.ACTIVATION_CASE]: (state, payload: IActivationCase[]) => {
        state.activationCase = payload;
    },

    [mutationTypes.ADD_MARKET]: (state, payload: IMarket) => {
        if (payload.parentId === null) {
            state.markets.push({ ...payload, children: [] });
        }
        else {
            const parentMarket = state.markets.find(market => market.id === payload.parentId);
            if (parentMarket != null) {
                parentMarket.children.push(payload);
                state.markets = [...state.markets];
            }
        }
    },

    [mutationTypes.ADD_BRAND]: (state, payload: IBrand) => {
        if (payload.parentId === null) {
            state.brands.push({ ...payload, children: [] });
        }
        else {
            const parentBrand = state.brands.find(brand => brand.id === payload.parentId);
            if (parentBrand != null) {
                parentBrand.children.push(payload);
                state.brands = [...state.brands];
            }
        }
    },

    [mutationTypes.ADD_BRANDQUALITY]: (state, payload: IBrandQuality) => {
        if (payload.parentId === null) {
            state.brandsQuality.push({ ...payload, children: [] });
        }
        else {
            const parentBrandQuality = state.brandsQuality.find(brandQuality => brandQuality.id === payload.parentId);
            if (parentBrandQuality != null) {
                parentBrandQuality.children.push(payload);
                state.brandsQuality = [...state.brandsQuality];
            }
        }
    },
    [mutationTypes.GET_INFLUENCERS_FILE_TEMPLATE]: (state, payload: Blob) => {
        state.influencersTemplate = payload;
    },
    [mutationTypes.GET_INFLUENCERS_FILE]: (state, payload: Blob) => {
        state.influencers = payload;
    },
    [mutationTypes.RESET_INFLUENCERS_FILE]: (state) => {
        state.influencers = null;
    },
    [mutationTypes.ADD_ACTIVATION]: (state, payload: IActivationCreate) => {
        state.activationCreate.push(payload);
    },
    [mutationTypes.UPLOAD_FILE]: (
        state,
        payload: IUploadFile,
    ) => {
        state.files.push(payload);
    },

    [mutationTypes.ADD_PR_AFFILIATE]: (state, payload: IPrAffiliate) => {
        state.prAffiliates.push(payload);
    },

    [mutationTypes.EDIT_ACTIVATION]: (state, payload: IActivationCreate) => {
        state.activationCreate.push(payload);
    },

    [mutationTypes.STORE_ACTIVATION]: (state, payload: IActivation) => {
        state.currentActivation = payload;
    },
    [mutationTypes.CLEAR_DASHBOARD]: (state) => {
        state.dashboard = [];
    },
    [mutationTypes.GET_ALL_ACTIVATIONS]: (state, payload: IActivation[]) => {
        state.activations = payload;
    },

    [mutationTypes.ACTIVATION_MARKET]: (state, payload: IMarket[]) => {
        state.markets = payload
            .map(market => ({ ...market, id: market.marketId, children: payload.filter(marketChild => market.marketId === marketChild.parentId).map(market => ({ ...market, id: market.marketId })) }))
            .filter(market => market.parentId === null)
    },

    [mutationTypes.ACTIVATION_BRAND]: (state, payload: IBrand[]) => {
        state.brands = payload
            .filter(brand => brand.parentId === null)
            .map(brand => ({ ...brand, id: brand.brandId, children: payload.filter(brandChild => brand.brandId === brandChild.parentId).map(brand => ({ ...brand, id: brand.brandId })) }));
    },

    [mutationTypes.ACTIVATION_PR_AFFILIATE]: (state, payload: IPrAffiliate[]) => {
        state.prAffiliates = payload
            .filter(pra => pra.parentId === null)
            .map(pra => ({ ...pra, prAffiliateId: pra.prAffiliateId, children: payload.filter(praChild => pra.prAffiliateId === praChild.parentId).map(pra => ({ ...pra, prAffiliateId: pra.prAffiliateId })) }));
    },

    [mutationTypes.ACTIVATION_BRAND_QUALITY]: (state, payload: IBrandQuality[]) => {
        state.brandsQuality = payload
            .filter(brandQuality => brandQuality.parentId === null)
            .map(brandQuality => ({ ...brandQuality, id: brandQuality.brandQualityId, children: payload.filter(brandChild => brandQuality.brandQualityId === brandChild.parentId).map(brand => ({ ...brand, id: brandQuality.brandQualityId })) }));
    },

    [mutationTypes.LOAD_ACTIVATIONCREATE]: (state, payload: IDashboard[]) => {
        state.dashboard = payload;
    },

    [mutationTypes.ADD_USER]: (state, payload: IUser) => {
        state.users.push(payload);
    },

    [mutationTypes.SET_USERS]: (state, payload: IUser[]) => {
        state.users = payload;
    },

    [mutationTypes.SET_ENTITIES]: (state, payload: IEntity[]) => {
        state.entities = payload;
    },

    [mutationTypes.SET_ENTITY_TYPES]: (state, payload: IEntityType[]) => {
        state.entityTypes = payload;
    },

    [mutationTypes.SET_ROLES]: (state, payload: IRole[]) => {
        state.roles = payload;
    },

    [mutationTypes.SET_UNAUTHORIZE_ROLES]: (state, payload: IRole[]) => {
        state.unauthorizeRoles = payload;
    },

    [mutationTypes.GET_ALL_KEYWORDS]: (state, payload: IKeyword[]) => {
        state.keywords = payload;
    },

    [mutationTypes.GET_ALL_HASHTAGS]: (state, payload: IHashtag[]) => {
        state.hashtags = payload;
    },

    [mutationTypes.UPDATE_USER]: (state, payload: IUser) => {
        const index = state.users.findIndex(
            user => user.userId === payload.userId,
        );
        state.users.splice(index, 1, payload);
    },
    [mutationTypes.SET_ACTIVATION_CAN_PROVIDER_ACCESS]: (state, payload: IActivationAccessUpdate) => {
        state.currentActivation.canProviderAccess = payload.canAccess;;
    },
    [mutationTypes.SET_ACTIVATION_IS_LIVE]: (state, payload: number) => {
        state.currentActivation.status = "Live";
        state.currentActivation.statusId = ActivationStatus.Live;
    },
    [mutationTypes.SET_ACTIVATION_DELETE]: (state, payload: number) => {
        state.currentActivation.status = "NA";
        state.currentActivation.statusId = ActivationStatus.NA;
    },
    [mutationTypes.LOAD_ACTIVATION_COMMENTS]: (state, payload: IActivationComment[]) => {
        state.activationComments = payload;
    },
    [mutationTypes.ADD_ACTIVATION_COMMENT]: (state, payload: IActivationComment) => {
        state.activationComments = [...state.activationComments, payload];
    },
    [mutationTypes.LOAD_ACTIVATIONS_USERS]: (state, payload: IActivationUser[]) => {
        state.activationUsers = payload;
    },
    [mutationTypes.UPSERT_ACTIVATION_USERS]: (state, payload: IActivationUser[]) => {
        state.upsertActivationUser = payload;
    },
};
